import { escapeHtml as escape } from '../util/escapeHtml.js';
/**
 * Themed confirmation dialog. Resolves true if the user confirms, false if
 * they cancel or press Escape / click outside.
 *
 * Replaces window.confirm everywhere a destructive action needs a sanity
 * check. The dialog blocks pointer events behind it and traps focus.
 */
export interface ConfirmOpts {
  title?: string;
  /** Body text — supports plain text. */
  message: string;
  /** Label on the confirm button. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Label on the cancel button. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Visual treatment: 'danger' makes the confirm button oxblood (default), 'gold' uses gold. */
  tone?: 'danger' | 'gold';
}

export function showConfirmDialog(opts: ConfirmOpts): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let resolved = false;
    const finish = (value: boolean): void => {
      if (resolved) return;
      resolved = true;
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      modal.remove();
      resolve(value);
    };

    const overlay = document.createElement('div');
    overlay.className = 'confirm-overlay';
    overlay.addEventListener('click', () => finish(false));
    document.body.append(overlay);

    const modal = document.createElement('div');
    modal.className = 'confirm-modal';
    modal.addEventListener('click', (e) => e.stopPropagation());
    document.body.append(modal);

    if (!document.getElementById('confirm-styles')) {
      const style = document.createElement('style');
      style.id = 'confirm-styles';
      style.textContent = CONFIRM_STYLES;
      document.head.append(style);
    }

    const title = opts.title ?? 'Are you sure?';
    const message = opts.message;
    const confirmLabel = opts.confirmLabel ?? 'Confirm';
    const cancelLabel = opts.cancelLabel ?? 'Cancel';
    const tone = opts.tone ?? 'danger';

    modal.innerHTML = `
      <h2 class="confirm-title">${escape(title)}</h2>
      <p class="confirm-message">${escape(message)}</p>
      <div class="confirm-actions">
        <button class="btn ghost" id="confirm-cancel">${escape(cancelLabel)}</button>
        <button class="btn ${tone === 'gold' ? 'gold' : ''}" id="confirm-yes">${escape(confirmLabel)}</button>
      </div>
    `;
    const yes = modal.querySelector<HTMLButtonElement>('#confirm-yes')!;
    const no = modal.querySelector<HTMLButtonElement>('#confirm-cancel')!;
    yes.addEventListener('click', () => finish(true));
    no.addEventListener('click', () => finish(false));

    // Focus the cancel button by default — safer for destructive actions.
    setTimeout(() => no.focus(), 0);

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') finish(false);
      if (e.key === 'Enter') finish(true);
    };
    document.addEventListener('keydown', onKey);
  });
}

/**
 * Themed "spoils" dialog — shows everything claimed from a kill on one screen,
 * one line per reward (crowns, XP, each item with its thumbnail). Used by the
 * Crypt claim so a boss kill's loot is clearly listed instead of flashing past
 * in a single toast. Resolves when dismissed.
 */
export interface SpoilsItem {
  /** Full reward line, e.g. "Gravebone ×3" or "12,400 crowns". */
  label: string;
  /** Item slug for a thumbnail (/img/item-thumbs/<slug>.webp). Omit for crowns/XP. */
  itemSlug?: string;
  /** Icon glyph for non-item rewards (crowns/XP). Optional. */
  glyph?: string;
}

export function showSpoilsDialog(opts: {
  title?: string;
  subtitle?: string;
  items: SpoilsItem[];
  closeLabel?: string;
}): Promise<void> {
  return new Promise<void>((resolve) => {
    let resolved = false;
    const finish = (): void => {
      if (resolved) return;
      resolved = true;
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      modal.remove();
      resolve();
    };

    const overlay = document.createElement('div');
    overlay.className = 'confirm-overlay';
    overlay.addEventListener('click', finish);
    document.body.append(overlay);

    const modal = document.createElement('div');
    modal.className = 'confirm-modal';
    modal.addEventListener('click', (e) => e.stopPropagation());
    document.body.append(modal);

    if (!document.getElementById('confirm-styles')) {
      const style = document.createElement('style');
      style.id = 'confirm-styles';
      style.textContent = CONFIRM_STYLES;
      document.head.append(style);
    }

    const title = opts.title ?? 'Spoils';
    const rows = opts.items.length
      ? opts.items
          .map((it) => {
            const icon = it.itemSlug
              ? `<img class="spoils-thumb" src="/img/item-thumbs/${escape(it.itemSlug)}.webp" alt="" onerror="this.style.visibility='hidden'" />`
              : `<span class="spoils-glyph">${escape(it.glyph ?? '◆')}</span>`;
            return `<li class="spoils-row">${icon}<span class="spoils-label">${escape(it.label)}</span></li>`;
          })
          .join('')
      : '<li class="spoils-row"><span class="spoils-label">Nothing this time — but the XP is yours.</span></li>';

    modal.innerHTML = `
      <h2 class="confirm-title">${escape(title)}</h2>
      ${opts.subtitle ? `<p class="confirm-message" style="margin-bottom:var(--s-3);">${escape(opts.subtitle)}</p>` : ''}
      <ul class="spoils-list">${rows}</ul>
      <div class="confirm-actions">
        <button class="btn gold" id="spoils-close">${escape(opts.closeLabel ?? 'Take it all')}</button>
      </div>
    `;
    const close = modal.querySelector<HTMLButtonElement>('#spoils-close')!;
    close.addEventListener('click', finish);
    setTimeout(() => close.focus(), 0);

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' || e.key === 'Enter') finish();
    };
    document.addEventListener('keydown', onKey);
  });
}

/**
 * Purchase-confirmation dialog — shown after a membership or Coin Shop chest
 * order is fulfilled, so the player must actively dismiss it and is never left
 * unsure the purchase landed. States exactly what was bought and where it went,
 * thanks them, and notes the funds keep the realm running. Reuses the confirm
 * chrome so it matches every other modal.
 */
export function showPurchaseDialog(opts: {
  title?: string;
  /** Main line — what was bought and where it landed. */
  detail: string;
  /** Optional second line — crowns to claim, charter expiry, etc. */
  sub?: string | undefined;
  closeLabel?: string;
}): Promise<void> {
  return new Promise<void>((resolve) => {
    let resolved = false;
    const finish = (): void => {
      if (resolved) return;
      resolved = true;
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      modal.remove();
      resolve();
    };

    const overlay = document.createElement('div');
    overlay.className = 'confirm-overlay';
    overlay.addEventListener('click', finish);
    document.body.append(overlay);

    const modal = document.createElement('div');
    modal.className = 'confirm-modal';
    modal.addEventListener('click', (e) => e.stopPropagation());
    document.body.append(modal);

    if (!document.getElementById('confirm-styles')) {
      const style = document.createElement('style');
      style.id = 'confirm-styles';
      style.textContent = CONFIRM_STYLES;
      document.head.append(style);
    }

    const body = opts.sub ? `${opts.detail}\n${opts.sub}` : opts.detail;
    modal.innerHTML = `
      <h2 class="confirm-title">${escape(opts.title ?? 'Purchase complete')}</h2>
      <p class="confirm-message">${escape(body)}</p>
      <p class="confirm-message" style="font-style: italic; color: var(--c-ink-soft); margin-bottom: var(--s-4);">Thank you for supporting Rex Mortis — every coin goes straight back into running and improving the game.</p>
      <div class="confirm-actions">
        <button class="btn gold" id="purchase-ok">${escape(opts.closeLabel ?? 'Confirm')}</button>
      </div>
    `;
    const ok = modal.querySelector<HTMLButtonElement>('#purchase-ok')!;
    ok.addEventListener('click', finish);
    setTimeout(() => ok.focus(), 0);

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' || e.key === 'Enter') finish();
    };
    document.addEventListener('keydown', onKey);
  });
}

/**
 * Themed input dialog — the in-world replacement for window.prompt. Resolves
 * with the entered string, or null if cancelled / Escape / click-outside.
 * Reuses the confirm-dialog chrome so every modal on the site looks the same.
 */
export interface PromptOpts {
  title?: string;
  /** Body text above the input — supports \n line breaks. */
  message: string;
  /** Pre-filled value. */
  defaultValue?: string;
  placeholder?: string;
  /** 'number' renders a numeric input; 'text' a text input. Default 'text'. */
  inputType?: 'text' | 'number';
  /** min/max/step for numeric inputs (optional). */
  min?: number;
  max?: number;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'gold';
}

export function showPromptDialog(opts: PromptOpts): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    let resolved = false;
    const finish = (value: string | null): void => {
      if (resolved) return;
      resolved = true;
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      modal.remove();
      resolve(value);
    };

    const overlay = document.createElement('div');
    overlay.className = 'confirm-overlay';
    overlay.addEventListener('click', () => finish(null));
    document.body.append(overlay);

    const modal = document.createElement('div');
    modal.className = 'confirm-modal';
    modal.addEventListener('click', (e) => e.stopPropagation());
    document.body.append(modal);

    if (!document.getElementById('confirm-styles')) {
      const style = document.createElement('style');
      style.id = 'confirm-styles';
      style.textContent = CONFIRM_STYLES;
      document.head.append(style);
    }

    const title = opts.title ?? 'Enter a value';
    const confirmLabel = opts.confirmLabel ?? 'Confirm';
    const cancelLabel = opts.cancelLabel ?? 'Cancel';
    const tone = opts.tone ?? 'gold';
    const t = opts.inputType ?? 'text';
    const attrs = [
      `type="${t}"`,
      opts.placeholder ? `placeholder="${escape(opts.placeholder)}"` : '',
      opts.min !== undefined ? `min="${opts.min}"` : '',
      opts.max !== undefined ? `max="${opts.max}"` : '',
      `value="${escape(opts.defaultValue ?? '')}"`,
    ].filter(Boolean).join(' ');

    modal.innerHTML = `
      <h2 class="confirm-title">${escape(title)}</h2>
      <p class="confirm-message">${escape(opts.message)}</p>
      <input class="confirm-input" ${attrs} />
      <div class="confirm-actions">
        <button class="btn ghost" id="confirm-cancel">${escape(cancelLabel)}</button>
        <button class="btn ${tone === 'gold' ? 'gold' : ''}" id="confirm-yes">${escape(confirmLabel)}</button>
      </div>
    `;
    const input = modal.querySelector<HTMLInputElement>('.confirm-input')!;
    const yes = modal.querySelector<HTMLButtonElement>('#confirm-yes')!;
    const no = modal.querySelector<HTMLButtonElement>('#confirm-cancel')!;
    yes.addEventListener('click', () => finish(input.value));
    no.addEventListener('click', () => finish(null));

    setTimeout(() => { input.focus(); input.select(); }, 0);

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') finish(null);
      if (e.key === 'Enter') finish(input.value);
    };
    document.addEventListener('keydown', onKey);
  });
}

const CONFIRM_STYLES = `
  .confirm-overlay {
    position: fixed;
    inset: 0;
    background: rgba(28, 17, 8, 0.55);
    z-index: 300;
    backdrop-filter: blur(1.5px);
  }
  .confirm-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(440px, 92vw);
    background: var(--c-parchment);
    background-image: var(--parchment-texture);
    border: 1px solid var(--c-rule-strong);
    border-radius: var(--radius-lg);
    box-shadow:
      0 0 0 1px var(--c-gold),
      0 14px 40px rgba(0, 0, 0, 0.5);
    padding: var(--s-5);
    z-index: 301;
  }
  .confirm-title {
    font-family: var(--font-heading);
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-size: 1.3rem;
    color: var(--c-oxblood);
    margin: 0 0 var(--s-3) 0;
    text-align: center;
  }
  .confirm-message {
    font-family: var(--font-body);
    font-size: 1.02rem;
    color: var(--c-ink);
    line-height: 1.5;
    margin: 0 0 var(--s-4) 0;
    text-align: center;
    /* Honour \n line breaks in the message string so callers can
       structure multi-line warnings (e.g. refill confirm dialog). */
    white-space: pre-line;
  }
  .confirm-actions {
    display: flex;
    justify-content: center;
    gap: var(--s-3);
  }
  /* Input inherits the global pinned cream cell (cream bg + dark ink, both
     themes) from pages.css — we only set layout here, never colours. */
  .confirm-input {
    display: block;
    width: 100%;
    box-sizing: border-box;
    font: inherit;
    font-family: var(--font-body);
    padding: 8px 12px;
    border: 1px solid var(--c-rule-strong);
    border-radius: var(--radius);
    margin: 0 0 var(--s-4) 0;
  }
  .spoils-list {
    list-style: none;
    margin: 0 0 var(--s-4) 0;
    padding: 0;
    max-height: 50vh;
    overflow-y: auto;
  }
  .spoils-row {
    display: flex;
    align-items: center;
    gap: var(--s-3);
    padding: 8px 10px;
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    background: rgba(255, 245, 220, 0.5);
    margin-bottom: 6px;
  }
  .spoils-row:last-child { margin-bottom: 0; }
  .spoils-thumb {
    width: 40px;
    height: 40px;
    object-fit: contain;
    flex: none;
  }
  .spoils-glyph {
    width: 40px;
    text-align: center;
    font-size: 1.3rem;
    color: var(--c-gold);
    flex: none;
  }
  .spoils-label {
    font-family: var(--font-body);
    font-size: 1.02rem;
    /* Cream rows stay light in both themes — pin dark ink so it never flips. */
    color: #1c1108;
  }
`;
